export type Education = {
  institution: string;
  location: string;
  degree: string;
  graduation: string;
  gpa: string;
  credits: string;
  courses: string[];
};

export type SeniorProject = {
  title: string;
  status: string;
  summary: string;
  methods: string[];
  outcomes: string[];
};

export type Publication = {
  title: string;
  venue: string;
  status: string;
  contribution: string;
};

export type Experience = {
  role: string;
  organization: string;
  dateRange: string;
  summary: string;
  highlights: string[];
};

export type Profile = {
  name: string;
  pronouns: string;
  role: string;
  location: string;
  email: string;
  phone: string;
  linkedin: string;
  headline: string;
  statement: string;
  availability: string;
  links: {
    label: string;
    href: string;
  }[];
};

export type About = {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  highlights: string[];
};

export type CvDetails = {
  contentVersion: number;
  eyebrow: string;
  title: string;
  deckTitle: string;
  description: string;
  previewLabel: string;
  downloadLabel: string;
  previewUrl: string;
  pdfUrl: string;
  updatedAt: string;
  methodsNote: CvMethodsNote;
  sections: CvSection[];
};

export type CvMethodsNote = {
  title: string;
  body: string;
  tools: string[];
};

export type CvItem = {
  title: string;
  meta?: string;
  date?: string;
  citation?: string;
  apa?: boolean;
  description?: string;
  bullets?: string[];
  tags?: string[];
};

export type CvSection = {
  id: string;
  title: string;
  kicker: string;
  summary: string;
  items: CvItem[];
};

export type PortfolioData = {
  public: boolean;
  profile: Profile;
  about: About;
  education: Education;
  awards: string[];
  memberships: string[];
  seniorProjects: SeniorProject[];
  publications: Publication[];
  experiences: Experience[];
  cv: CvDetails;
};

export const profile: Profile = {
  name: "Xander Marshall",
  pronouns: "B.S. Psychology | Research Emphasis",
  role: "Research Assistant",
  location: "San Luis Obispo, CA",
  email: "xm@xandermarshall.com",
  phone: "",
  linkedin: "https://www.linkedin.com/in/xander-marshall",
  headline: "Xander Marshall",
  statement:
    "I am a psychology student at Cal Poly San Luis Obispo and a research assistant working on prevention and intervention research.",
  availability:
    "I am interested in assessment, prevention, and intervention research.",
  links: [
    {
      label: "View CV",
      href: "#cv",
    },
    { label: "LinkedIn", href: "https://www.linkedin.com/in/xander-marshall" },
    { label: "Email", href: "mailto:xm@xandermarshall.com" },
  ],
};

export const about: About = {
  eyebrow: "About",
  title: "About Me",
  paragraphs: [
    "I am graduating from Cal Poly San Luis Obispo in June 2026 with a B.S. in Psychology, Research Emphasis. My research interests focus on assessment, prevention, and intervention, especially in developmental and educational settings. I am interested in how research can be used to better understand risk, support, and effective intervention.",
  ],
  highlights: [
    "Research Assistant",
    "Assessment",
    "Prevention and Intervention",
  ],
};

export const education: Education = {
  institution: "California Polytechnic State University",
  location: "San Luis Obispo, CA",
  degree: "B.S. in Psychology, Research Emphasis",
  graduation: "June 2026",
  gpa: "3.91",
  credits: "217 credits earned",
  courses: [
    "Calculus I-III",
    "Applied Experimental Design and Regression Models",
    "Quantitative Research Methods",
    "Forensic Psychology",
    "Abnormal Psychology",
    "The Helping Relationship",
    "Behavior in Organizations",
  ],
};

export const seniorProjects: SeniorProject[] = [
  {
    title: "Drama-Based Upstander Intervention",
    status: "SPSP Intervention Science Preconference Presentation",
    summary:
      "Project on a drama-based intervention designed to support upstander behavior in elementary school classrooms.",
    methods: [
      "Survey Coding",
      "Scale Preparation",
      "Conference Materials",
    ],
    outcomes: [
      "Prepared survey data and variables for analysis.",
      "Helped prepare materials for a conference presentation.",
    ],
  },
  {
    title: "Bullying in Elementary School Classrooms",
    status: "SRCD Biennial Meeting Poster",
    summary:
      "Poster on classroom bullying and prevention in elementary school settings.",
    methods: [
      "Data Validation",
      "Literature Reviews",
      "Poster Development",
    ],
    outcomes: [
      "Supported data checks and literature review work.",
      "Helped develop poster materials for the conference.",
    ],
  },
];

export const publications: Publication[] = [
  {
    title: "Drama-based upstander intervention: An elementary-school-based field experiment",
    venue:
      "Presentation at the Society for Personality and Social Psychology Intervention Science Preconference, Chicago, IL",
    status:
      "Langner, C., Lee, L., Ovadia, R., Armistead, L., Marshall, X., Patel, N., & Madonna, G. (2026)",
    contribution:
      "Supported survey coding, data preparation, analysis, and presentation materials.",
  },
  {
    title: "Bullying in Elementary School Classrooms",
    venue:
      "Poster presented at the Society for Research in Child Development Biennial Meeting, Minneapolis, MN",
    status: "Langner, C., et al. (2025)",
    contribution:
      "Supported data validation, literature review work, and poster development.",
  },
];

export const experiences: Experience[] = [
  {
    role: "Research Assistant",
    organization: "Carrie Langner, PhD and Linda Lee, PhD",
    dateRange: "November 2024 - Present",
    summary:
      "Assist with faculty-led psychology research on classroom bullying and upstander intervention.",
    highlights: [
      "Code survey data and prepare scales for analysis.",
      "Use SPSS, JMP, R, and Excel to support statistical analyses.",
      "Prepare literature reviews, posters, and presentation materials.",
    ],
  },
  {
    role: "Fleet Attendant",
    organization: "Starship Technologies | San Luis Obispo, CA",
    dateRange: "September 2024 - June 2026",
    summary:
      "Supported daily autonomous delivery fleet operations.",
    highlights: [
      "Tracked operational data and documented issues.",
      "Trained new team members on procedures and troubleshooting.",
    ],
  },
  {
    role: "Task Associate",
    organization: "Ulta Beauty | San Luis Obispo, CA",
    dateRange: "June 2024 - June 2026",
    summary:
      "Supported inventory and store operations.",
    highlights: [
      "Maintained inventory records through audits, cycle counts, and discrepancy resolution.",
      "Balanced changing priorities while maintaining task accuracy.",
    ],
  },
];

export const awards = [
  "Dean's List, every quarter",
  "President's List, every year",
  "Summa Cum Laude",
  "Empirical Research Psychological Sciences Scholarship",
  "Center for Advanced Research and Technology Scholarship",
];

export const memberships = [
  "Society for Personality and Social Psychology",
  "American Choral Directors' Association",
];

export const cv: CvDetails = {
  contentVersion: 3,
  eyebrow: "",
  title: "Curriculum Vitae",
  deckTitle: "Xander Marshall CV",
  description: "",
  previewLabel: "Open Full PDF",
  downloadLabel: "Download PDF",
  previewUrl: "/assets/xander-marshall-cv.pdf",
  pdfUrl: "/assets/xander-marshall-cv.pdf",
  updatedAt: "May 2026",
  methodsNote: {
    title: "How I Work With Data",
    body:
      "The technical side of the work is there to serve the question: clean survey data, prepared scales, reproducible analyses, and clear materials for presentations and documentation.",
    tools: ["R", "SPSS", "JMP", "Excel", "Qualtrics", "Microsoft 365"],
  },
  sections: [
    {
      id: "education",
      title: "Education",
      kicker: "",
      summary: "",
      items: [
        {
          title: "California Polytechnic State University",
          meta: "San Luis Obispo, CA",
          date: "September 2024 - June 2026",
          description: "B.S. in Psychology, Research Emphasis",
          bullets: [
            "Admitted as a freshman in 2024, with dual enrollment and Advanced Placement credits applied toward graduation.",
            "Coursework includes Applied Experimental Design and Regression Models, Quantitative Research Methods, Forensic Psychology, Abnormal Psychology, The Helping Relationship, Behavior in Organizations, and Calculus.",
          ],
          tags: ["Research Emphasis", "Expected June 2026"],
        },
      ],
    },
    {
      id: "research-interests",
      title: "Research Interests",
      kicker: "",
      summary: "",
      items: [
        {
          title: "Development, Prevention, and Long-Term Impact",
          description:
            "I am interested in developmental psychopathology and in evidence-based prevention and intervention programs that aim to reduce psychological distress and mental illness in children. I am especially drawn to the longer arc of those programs: whether early support continues to matter later in development and adulthood.",
          tags: [
            "Developmental Psychopathology",
            "Prevention",
            "Intervention",
            "Longitudinal Impact",
          ],
        },
      ],
    },
    {
      id: "research-experience",
      title: "Research Experience",
      kicker: "",
      summary: "",
      items: [
        {
          title: "Research Assistant",
          meta: "Carrie Langner, PhD and Linda Lee, PhD",
          date: "November 2024 - Present",
          bullets: [
            "Entered and checked paper survey data in SPSS and Excel before analysis.",
            "Prepared variables for regression models by reverse-coding items and trimming scale values when needed.",
            "Reviewed relevant literature to connect study questions to previous work.",
            "Supported analyses of primary, mediator, and moderator effects using SPSS, JMP, and R.",
            "Helped turn findings into posters and presentations for conference audiences.",
            "Contributed to discussions about study design, hypotheses, and interpretation.",
          ],
          tags: ["Survey Data", "Regression Models", "Literature Review", "Conference Materials"],
        },
      ],
    },
    {
      id: "projects",
      title: "Projects and Presentations",
      kicker: "",
      summary: "",
      items: [
        {
          title: "Drama-Based Upstander Intervention",
          citation:
            "Langner, C., Lee, L., Ovadia, R., Armistead, L., Marshall, X., Patel, N., & Madonna, G. (2026). Drama-based upstander intervention: An elementary-school-based field experiment. Presentation at the Society for Personality and Social Psychology (SPSP) Intervention Science Preconference, Chicago, IL",
          apa: true,
          bullets: [
            "Supported implementation of a drama-based bullying intervention and helped evaluate outcomes among 3rd-grade (n=142) and 4th-grade (n=211) students across 6 California elementary schools.",
            "Used multivariate regression models to examine behavioral outcomes and upstander behavior.",
          ],
          tags: ["SPSP", "Regression", "Bullying Prevention"],
        },
        {
          title: "Senior Project",
          citation:
            "Ovadia, R., Marshall, X., Patel, N. (2026). A Senior Project submitted in partial fulfillment of the requirements for the Bachelor of Science Degree in Psychology.",
          apa: true,
          bullets: [
            "Examined self-efficacy and social norms as mediator variables in a drama-based intervention.",
            "Prepared SPSS analyses, literature review materials, and thesis documentation.",
          ],
          tags: ["Senior Project", "Mediation", "SPSS"],
        },
      ],
    },
    {
      id: "work",
      title: "Work Experience",
      kicker: "",
      summary: "",
      items: [
        {
          title: "Fleet Attendant",
          meta: "Starship Technologies | San Luis Obispo, CA",
          date: "Aug. 2024 - June 2026",
          bullets: [
            "Monitored and maintained autonomous delivery robots while coordinating with merchants to keep deliveries moving smoothly.",
            "Troubleshot technical and operational issues that affected fleet health.",
            "Handled opening and closing procedures, including inventory and equipment checks.",
            "Communicated with customers and merchants in a professional, practical way.",
            "Worked independently while balancing efficiency, safety, and continuity of service.",
          ],
          tags: ["Operations", "Troubleshooting", "Autonomous Delivery"],
        },
        {
          title: "Task Associate",
          meta: "Ulta Beauty | San Luis Obispo and Fresno, CA",
          date: "June 2024 - Present",
          bullets: [
            "Supported inventory organization, restocking, and merchandising during daily store operations.",
            "Worked with team members to keep the store organized and efficient.",
            "Helped customers locate products and understand basic product use.",
            "Trained new team members and helped management work through store-level challenges.",
          ],
          tags: ["Inventory", "Training", "Customer Support"],
        },
      ],
    },
    {
      id: "honors",
      title: "Honors and Memberships",
      kicker: "",
      summary: "",
      items: [
        {
          title: "Honors and Awards",
          bullets: [
            "Empirical Research Psychological Sciences Scholarship, 2026.",
            "President's List, 2024-2026.",
            "Dean's List (Multiple Quarters), 2024-2026.",
            "Summa Cum Laude, 2026.",
          ],
          tags: ["Scholarship", "President's List", "Dean's List"],
        },
        {
          title: "Professional Membership",
          bullets: [
            "Member, Society for Personality and Social Psychology (SPSP).",
            "Member, American Choral Directors Association (ACDA).",
          ],
          tags: ["SPSP", "ACDA"],
        },
      ],
    },
  ],
};

export const fallbackPortfolioData: PortfolioData = {
  public: true,
  profile,
  about,
  education,
  awards,
  memberships,
  seniorProjects,
  publications,
  experiences,
  cv,
};
